import React, { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { SwiperSlide } from 'swiper/react';
import { CourseCard } from 'src/common/components/CourseCard';
import { media } from 'src/config/breakpoints';
import { SectionTitle } from 'src/common/components/SectionTitle';
import { CourseCardSwiper, NavigationButton } from 'src/common/components/CourseCardSwiper';
import { layers } from 'src/config/layers';
import { StyleProps } from 'src/lib/styleProps';
import { useRelatedCoursesQuery } from 'src/common/hooks/course/useRelatedCoursesQuery';
import { SimpleContentLoader } from 'src/common/components/SimpleContentLoader';
import { mergeDeepRight } from 'ramda';
import { FreeMode, SwiperOptions } from 'swiper';

const Card = styled.div`
  width: 13.25rem;

  ${media.w.min.px768(css`
    width: auto;
  `)};
`;

const Container = styled.div`
  position: relative;
`;

const StyledSectionTitle = styled(SectionTitle)`
  margin-bottom: -0.875rem;
  position: relative;
  text-align: center;
  z-index: ${layers.contentBottom};
`;

const StyledCourseCardSwiper = styled(CourseCardSwiper)`
  ${NavigationButton} {
    ${media.w.max.px992(css`
      display: none;
    `)};
  }

  .swiper-scrollbar {
    ${media.w.min.px992(css`
      display: none;
    `)};
  }

  .swiper-slide {
    width: auto;
  }
`;

const SingleCard = styled.div`
  margin-top: 2.375rem;
`;

const baseSwiperOptions: SwiperOptions = {
  breakpoints: {
    768: {
      centeredSlides: false,
      centeredSlidesBounds: false,
      freeMode: {
        enabled: false,
      },
    },
    992: {
      centeredSlides: false,
      centeredSlidesBounds: false,
      freeMode: {
        enabled: false,
      },
    },
    1200: {
      centeredSlides: false,
      centeredSlidesBounds: false,
      freeMode: {
        enabled: false,
      },
    },
  },
  centeredSlides: true,
  centeredSlidesBounds: true,
  freeMode: {
    enabled: true,
  },
  modules: [FreeMode],
  slidesPerView: 'auto',
};

export interface RelatedCoursesProps {
  courseId?: number;
  swiperOptions: SwiperOptions;
}

export const RelatedCourses: FC<RelatedCoursesProps & StyleProps> = ({
  className,
  courseId,
  swiperOptions,
}) => {
  const { isLoading, data } = useRelatedCoursesQuery(courseId);

  const options = useMemo(
    () => mergeDeepRight(baseSwiperOptions, swiperOptions) as SwiperOptions,
    [swiperOptions],
  );

  if (!isLoading && (!data || data.length === 0)) {
    return null;
  }

  return (
    <Container className={className}>
      {isLoading && <SimpleContentLoader />}

      {!isLoading && data && (
        <>
          <StyledSectionTitle>Podobne szkolenia</StyledSectionTitle>

          {data?.length === 1 && (
            <SingleCard>
              <CourseCard courseExcerpt={data[0]} featured={true} />
            </SingleCard>
          )}

          {data?.length > 1 && (
            <StyledCourseCardSwiper options={options}>
              {data?.map((courseExcerpt) => (
                <SwiperSlide key={courseExcerpt.courseId}>
                  <Card>
                    <CourseCard courseExcerpt={courseExcerpt} featured={false} />
                  </Card>
                </SwiperSlide>
              ))}
            </StyledCourseCardSwiper>
          )}
        </>
      )}
    </Container>
  );
};
